import PropTypes from "prop-types";
import React, { useState, useEffect, useMemo } from "react";
import {
  Radio,
  Box,
  ClickAwayListener,
  ListItemIcon,
  ListItem as MuiListItem,
  Divider,
  ListItemText,
  IconButton,
  Button,
  Typography,
  FormHelperText
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { Field, FieldArray, useFormikContext } from "formik";
import { useTheme } from "@mui/material/styles";
import DraggableList from "../../DraggableList/DraggableList";
import HTMLEditor from "../../HtmlEditor/HtmlEditor";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add
} from "@mui/icons-material";
import clsx from "clsx";
import { CheckboxWithLabel } from "formik-mui";
import QuestionTextField from "./QuestionTextField";
import AnswersField from "./AnswersField";

const useStyles = makeStyles(theme => ({
  divider: {
    marginLeft: 102
  },
  iconButton: {
    opacity: 0,
    transition: "opacity 300ms"
  },
  listItemGutters: {
    padding: theme.spacing(0, 0, 0, 1.5)
  },
  listItem: {
    backgroundColor: theme.palette.inputBackground,
    alignItems: "flex-start",
    "&:hover $iconButton": {
      opacity: 1
    }
  },
  listItemText: {
    margin: theme.spacing(0, 0, 0, 2.25)
  },
  listItemIcon: {
    height: 56
  },
  secondaryAction: {
    position: "absolute",
    height: "100%",
    right: 0,
    zIndex: 1
  },
  editable: {
    "& $secondaryAction": {
      height: "unset"
    },
    "& $listItemText": {
      backgroundColor: theme.palette.action.hover,
      border: "solid 1px rgba(0, 0, 0, 0.12)",
      marginTop: -1,
      marginBottom: -1
    },
    "& .tox .tox-edit-area .tox-edit-area__iframe": {
      backgroundColor: "rgba(0, 0, 0 ,0)"
    }
  }
}));

const HtmlAnswerChoice = ({
  onChange,
  value,
  editable,
  onDialogOpenChange,
  index,
  questionFilesList,
  setQuestionFilesList
}) => {
  const { t } = useTranslation();

  const placeholder = t("editQuestion.answerOptionPlaceholder", {
    index: index + 1
  });
  return editable ? (
    <Box flex={1}>
      <HTMLEditor
        disableMargin
        disableUnderline
        height={150}
        onChange={onChange}
        value={value}
        onFileAdd={(fileObj, tempLink, type) => {
          const list = questionFilesList;
          list.push({ fileObj, tempLink, type });
          setQuestionFilesList(list);
        }}
        menubar={false}
        plugins={["lists", "link", "image", "media", "code"]}
        toolbar="undo redo | bold italic underline | alignment numlist bullist outdent indent | link image media fileUpload"
        disableResize
        autoFocus
        onDialogOpenChange={onDialogOpenChange}
        placeholder={placeholder}
      />
    </Box>
  ) : (
    <Box
      py={2}
      dangerouslySetInnerHTML={{ __html: value ? value : placeholder }}
    />
  );
};

const MultipleChoiceResponse = ({
  response,
  index,
  onDeleteResponse,
  onCorrectAnswerChange,
  selectDisabled,
  showDivider,
  onAnswerTextChange,
  dragHandle,
  showRadioButton,
  isDraggingOver,
  questionFilesList,
  setQuestionFilesList
}) => {
  const classes = useStyles();
  const [editClicked, setEditClicked] = useState(false);
  const [answerText, setAnswerText] = useState("");
  const [editorDialogOpen, setEditorDialogOpen] = useState(false);

  useEffect(() => {
    setAnswerText(response.text);
  }, [response]);

  const handleClickAway = () => {
    if (!editorDialogOpen) {
      setEditClicked(false);
      onAnswerTextChange(answerText, index);
    }
  };

  const changeAnswerText = (text, index) => {
    onAnswerTextChange(text, index);
    setAnswerText(text);
  };

  const listItem = useMemo(
    () => (
      <MuiListItem
        classes={{
          gutters: classes.listItemGutters,
          root: clsx(classes.listItem, {
            [classes.editable]: editClicked,
            [classes.isDraggingOver]: isDraggingOver
          }),
          secondaryAction: classes.secondaryAction
        }}
      >
        <ListItemIcon className={classes.listItemIcon}>
          <Box display="flex" alignItems="center">
            {dragHandle}
            {!showRadioButton && (
              <Radio
                checked={response.correct}
                onChange={() => onCorrectAnswerChange(index)}
                disabled={selectDisabled}
                color="primary"
                inputProps={{
                  "data-qa": "answer-choices-radio-btn"
                }}
              />
            )}
          </Box>
        </ListItemIcon>
        <ListItemText
          className={classes.listItemText}
          primary={
            <Box display="flex" alignItems="flex-start">
              <HtmlAnswerChoice
                value={answerText}
                editable={editClicked}
                onChange={text => changeAnswerText(text, index)}
                index={index}
                onDialogOpenChange={dialogOpen =>
                  setEditorDialogOpen(dialogOpen)
                }
                questionFilesList={questionFilesList}
                setQuestionFilesList={setQuestionFilesList}
              />
              {!isDraggingOver && (
                <Box
                  className={classes.secondaryAction}
                  display="flex"
                  alignItems="center"
                >
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => setEditClicked(!editClicked)}
                    data-qa="answer-choices-edit-btn"
                    size="large"
                  >
                    <EditIcon color="primary" />
                  </IconButton>
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => onDeleteResponse(index)}
                    size="large"
                  >
                    <DeleteIcon color="primary" />
                  </IconButton>
                </Box>
              )}
            </Box>
          }
          primaryTypographyProps={{
            variant: "subtitle1"
          }}
        />
      </MuiListItem>
    ),
    [answerText, editClicked, index, response, isDraggingOver]
  );

  return (
    <>
      {editClicked ? (
        <ClickAwayListener onClickAway={handleClickAway}>
          {listItem}
        </ClickAwayListener>
      ) : (
        listItem
      )}
      {showDivider && <Divider className={classes.divider} />}
    </>
  );
};
const MultipleChoiceFields = ({
  onCorrectAnswerChange,
  onAnswerTextChange,
  onReorderAnswers,
  onAddAnswer,
  isSurveyQuestion,
  questionFilesList,
  setQuestionFilesList,
  onDelete
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { errors, submitCount, values } = useFormikContext();

  return (
    <>
      <QuestionTextField
        questionFilesList={questionFilesList}
        setQuestionFilesList={setQuestionFilesList}
      />
      <Box flex={1} mt={5}>
        <AnswersField
          renderAnswers={() => (
            <Box display="flex" flexDirection="column" flex={1}>
              <Box
                bgcolor={theme.palette.inputBackground}
                pr={3}
                py={1.5}
                flex={1}
              >
                <FieldArray
                  name="answers"
                  render={arrayHelpers => (
                    <DraggableList
                      onDragEnd={onReorderAnswers}
                      droppableId="multiple-choice-responses"
                      items={values.answers.map((answer, index) => ({
                        draggableId: answer.id,
                        render: ({ dragHandle, isDraggingOver }) => (
                          <MultipleChoiceResponse
                            response={answer}
                            index={index}
                            dragHandle={dragHandle}
                            showDivider={index !== values.answers.length - 1}
                            onDeleteResponse={onDelete}
                            onCorrectAnswerChange={onCorrectAnswerChange}
                            onAnswerTextChange={onAnswerTextChange}
                            showRadioButton={isSurveyQuestion}
                            isDraggingOver={isDraggingOver}
                            questionFilesList={questionFilesList}
                            setQuestionFilesList={setQuestionFilesList}
                          />
                        )
                      }))}
                    />
                  )}
                />
              </Box>
              {!!errors.answers && submitCount > 0 && (
                <Box ml={1.75} mt={0.25}>
                  <FormHelperText error={true}>{errors.answers}</FormHelperText>
                </Box>
              )}
              <Box my={4}>
                <Button
                  color="primary"
                  onClick={onAddAnswer}
                  startIcon={<Add />}
                >
                  {t("editQuestion.addAnswerBtn")}
                </Button>
              </Box>
            </Box>
          )}
        />
      </Box>
      <Box mb={2}>
        <Field
          component={CheckboxWithLabel}
          name="randomizeAnswers"
          inputProps={{
            "data-qa": "randomize-answers-checkbox"
          }}
          Label={{
            label: (
              <Typography variant="subtitle1">
                {t("editQuestion.randomizeAnswersLabel")}
              </Typography>
            )
          }}
          color="primary"
          type="checkbox"
        />
      </Box>
    </>
  );
};

MultipleChoiceFields.propTypes = {
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      correct: PropTypes.bool
    })
  ),
  isSurveyQuestion: PropTypes.bool,
  onAddAnswer: PropTypes.func,
  onCorrectAnswerChange: PropTypes.func,
  onAnswerTextChange: PropTypes.func,
  onReorderAnswers: PropTypes.func,
  questionFilesList: PropTypes.array,
  setQuestionFilesList: PropTypes.func,
  onDelete: PropTypes.func
};

export default MultipleChoiceFields;
